import { useState, useEffect } from "react";

interface ProcessingStepProps {
  isDemo: boolean;
  onComplete: () => void;
}

const STEPS = [
  {
    id: "ingest",
    label: "시청자 신호 분석 중",
    desc: "동시 시청자 수, 채팅 밀도, 반응 속도 데이터 수집",
    duration: 2400,
  },
  {
    id: "detect",
    label: "하이라이트 구간 감지 중",
    desc: "참여 급등 구간 및 감정 피크 포인트 식별",
    duration: 2800,
  },
  {
    id: "edit",
    label: "AI 편집 시스템 실행 중",
    desc: "불필요 구간 제거 및 내러티브 흐름 기반 동적 컷 생성",
    duration: 3200,
  },
  {
    id: "optimize",
    label: "숏폼 최적화 처리 중",
    desc: "9:16 변환, 자막 생성, 훅 구간 강화 적용",
    duration: 2000,
  },
];

type StepStatus = "pending" | "active" | "done";

export default function ProcessingStep({ isDemo, onComplete }: ProcessingStepProps) {
  const [stepStatuses, setStepStatuses] = useState<StepStatus[]>(["active", "pending", "pending", "pending"]);
  const [stepProgress, setStepProgress] = useState<number[]>([0, 0, 0, 0]);
  const [currentStep, setCurrentStep] = useState(0);
  const [logs, setLogs] = useState<string[]>([
    `[00:00] ${isDemo ? "데모 영상 로드 완료 — Riot Games LCK 2024 Worlds (47:22)" : "영상 파일 로드 완료 — AI 파이프라인 초기화"}`,
  ]);

  const LOG_LINES: Record<string, string[]> = {
    ingest: [
      "[00:02] 채팅 스트림 파싱 중... 24,847개 메시지 처리",
      "[00:04] 참여도 급등 구간 12개 검출 → 타임스탬프 동기화",
      "[00:06] 감정 분석 완료: 긍정 68% / 흥분 24% / 중립 8%",
    ],
    detect: [
      "[00:08] 하이라이트 후보 세그먼트 탐색 중...",
      "[00:10] 참여 스코어 기반 랭킹 산출 — 상위 7개 구간 선정",
      "[00:12] 피크 타임스탬프: 08:14 / 17:52 / 31:08 / 44:37",
    ],
    edit: [
      "[00:14] 저가치 구간 자동 제거 (무음 17초 / 대기 43초)",
      "[00:16] 내러티브 흐름 분석 완료 — 5개 컷 포인트 생성",
      "[00:19] AI 편집 결과: 47:22 → 4:17 (최종 하이라이트 릴)",
    ],
    optimize: [
      "[00:22] 9:16 세로형 포맷 변환 완료",
      "[00:24] 자막 자동 생성 — 한국어 / 영어 병렬 처리",
      "[00:26] 훅 구간 강화 적용 (첫 1.8초 최적화)",
    ],
  };

  useEffect(() => {
    let stepIdx = 0;
    const totalSteps = STEPS.length;

    const runStep = (idx: number) => {
      if (idx >= totalSteps) {
        setTimeout(onComplete, 600);
        return;
      }
      const dur = STEPS[idx].duration;
      const logLines = LOG_LINES[STEPS[idx].id];
      const logInterval = dur / (logLines.length + 1);

      logLines.forEach((line, i) => {
        setTimeout(() => {
          setLogs((prev) => [...prev.slice(-8), line]);
        }, logInterval * (i + 1));
      });

      const progressInterval = setInterval(() => {
        setStepProgress((prev) => {
          const next = [...prev];
          const newVal = Math.min(next[idx] + Math.floor(Math.random() * 8) + 3, 100);
          next[idx] = newVal;
          if (newVal >= 100) {
            clearInterval(progressInterval);
            setStepStatuses((prev2) => {
              const ns = [...prev2];
              ns[idx] = "done";
              if (idx + 1 < totalSteps) ns[idx + 1] = "active";
              return ns;
            });
            setCurrentStep(idx + 1);
            setTimeout(() => runStep(idx + 1), 300);
          }
          return next;
        });
      }, dur / 30);
    };

    runStep(stepIdx);
  }, []);

  const overallProgress = Math.round(stepProgress.reduce((a, b) => a + b, 0) / STEPS.length);

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] text-xs font-semibold px-4 py-2 rounded-full mb-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse inline-block" />
          AI 처리 중
        </div>
        <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          AI가 하이라이트를 분석하고 있습니다
        </h2>
        <p className="text-gray-400 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
          4단계 AI 파이프라인이 실행 중입니다 · 잠시만 기다려주세요
        </p>
      </div>

      {/* Overall progress */}
      <div className="bg-[#0F1419] border border-white/8 rounded-2xl p-6 mb-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white font-semibold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>전체 진행률</span>
          <span className="text-[#00D9FF] font-bold text-lg" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{overallProgress}%</span>
        </div>
        <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${overallProgress}%`,
              background: "linear-gradient(90deg, #00D9FF, #10B981)",
              boxShadow: "0 0 12px rgba(0,217,255,0.4)",
            }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3 mb-5">
        {STEPS.map((step, idx) => {
          const status = stepStatuses[idx];
          const progress = stepProgress[idx];
          return (
            <div
              key={step.id}
              className={`bg-[#0F1419] border rounded-xl p-4 transition-all duration-300 ${
                status === "active"
                  ? "border-[#00D9FF]/50 shadow-[0_0_20px_rgba(0,217,255,0.08)]"
                  : status === "done"
                  ? "border-[#10B981]/30"
                  : "border-white/5 opacity-50"
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${
                  status === "done" ? "bg-[#10B981]/20" : status === "active" ? "bg-[#00D9FF]/15" : "bg-white/5"
                }`}>
                  {status === "done" ? (
                    <i className="ri-check-line text-[#10B981] text-sm" />
                  ) : status === "active" ? (
                    <i className="ri-loader-4-line text-[#00D9FF] text-sm animate-spin" />
                  ) : (
                    <span className="text-gray-600 text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{idx + 1}</span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-semibold ${status === "pending" ? "text-gray-600" : "text-white"}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {step.label}
                    </span>
                    {status !== "pending" && (
                      <span className={`text-xs font-bold ${status === "done" ? "text-[#10B981]" : "text-[#00D9FF]"}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {status === "done" ? "완료" : `${progress}%`}
                      </span>
                    )}
                  </div>
                  {status !== "pending" && (
                    <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>{step.desc}</p>
                  )}
                </div>
              </div>
              {status === "active" && (
                <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden ml-11">
                  <div
                    className="h-full rounded-full transition-all duration-200"
                    style={{ width: `${progress}%`, background: "linear-gradient(90deg, #00D9FF, #10B981)" }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Live log terminal */}
      <div className="bg-[#050709] border border-white/8 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
            <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
            <span className="w-3 h-3 rounded-full bg-[#28C840]" />
          </div>
          <span className="text-gray-600 text-xs ml-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>AI Pipeline Log</span>
        </div>
        <div className="space-y-1 max-h-32 overflow-hidden">
          {logs.map((log, i) => (
            <p key={i} className={`text-xs font-mono leading-relaxed ${i === logs.length - 1 ? "text-[#00D9FF]" : "text-gray-500"}`}>
              {log}
            </p>
          ))}
          <span className="inline-block w-2 h-3.5 bg-[#00D9FF] animate-pulse align-middle" />
        </div>
      </div>
    </div>
  );
}
